import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Link, useLocation } from 'react-router-dom';
import './App.css';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { AuthProvider, useAuth } from './AuthContext';
import { SiteStatsProvider } from './SiteStatsContext';
import { AnimateOnScroll } from './hooks/useScrollAnimation';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import MainCTA from './components/MainCTA';
import AdvantagesSection from './components/AdvantagesSection';
import CompatibleGamesSection from './components/CompatibleGamesSection';
import BenchmarkPreview from './components/BenchmarkPreview';
import FinalCTA from './components/FinalCTA';
import ServicesPage from './components/ServicesPage';
import BenchmarkPage from './components/BenchmarkPage';
import FPSEstimatorPage from './components/FPSEstimatorPage';
import FAQPage from './components/FAQPage';
import FeedbacksPage from './components/FeedbacksPage';
import MesCommandesPage from './components/MesCommandesPage';
import AdminPage from './components/AdminPage';
import EspaceClientPage from './components/EspaceClientPage';
import AqmOptiToolPage from './components/AqmOptiToolPage';
import ReferralPage from './components/ReferralPage';
import InviteRedirect from './components/InviteRedirect';
import AffiliatesManager from './components/AffiliatesManager';
import MyAffiliatePage from './components/MyAffiliatePage';
import SuccessPage from './components/SuccessPage';
import RecruitmentPage from './components/RecruitmentPage';
import SuggestionsPage from './components/SuggestionsPage';
import HelperSpacePage from './components/HelperSpacePage';
import SupportPage from './components/SupportPage';
import MaLicencePage from './components/MaLicencePage';
import LicencesPage from './components/LicencesPage';
import AnimatedBackground from './components/AnimatedBackground';
import ParticlesBackground from './components/ParticlesBackground';
import FlashSaleBanner from './components/FlashSaleBanner';
import LiveFlashBar from './components/LiveFlashBar';
import StickyCTABanner from './components/StickyCTABanner';
import DiscordRequiredModal from './components/DiscordRequiredModal';
import MaintenanceGate from './components/MaintenanceGate';
import { TermsPage, PrivacyPage, LegalNoticePage, SalesTermsPage } from './components/LegalPages';
import { Settings, TrendingUp, Activity, Star, ChevronRight, Zap, Shield, Clock, CheckCircle, X, ArrowRight, LogIn, Ban } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IP Ban Check Component
const BannedPage = () => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
    <div className="max-w-md w-full text-center">
      <div className="w-24 h-24 bg-red-600/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
        </svg>
      </div>
      <h1 className="text-3xl font-bold text-white mb-4">Service Indisponible</h1>
      <p className="text-gray-400 mb-2">
        Une erreur est survenue lors du chargement de la page.
      </p>
      <p className="text-gray-500 text-sm mb-6">
        Code erreur: 503
      </p>
      <a 
        href="https://discord.gg/aqmopti" 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-lg transition-all"
      >
        Contacter le support
      </a>
    </div>
  </div>
);

// IP Ban Check Hook
const useIPBanCheck = () => {
  const [isBanned, setIsBanned] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkBan = async () => {
      try {
        // Try to access a protected endpoint
        const response = await fetch(`${API_URL}/api/stripe/public-key`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store' // Prevent caching
        });
        
        console.log('Ban check response status:', response.status);
        
        if (response.status === 403) {
          try {
            const data = await response.json();
            console.log('Ban check data:', data);
            if (data.error === 'ip_banned') {
              setIsBanned(true);
              return;
            }
          } catch (e) {
            // If can't parse JSON but got 403, still consider banned
            console.log('403 received, considering as banned');
            setIsBanned(true);
            return;
          }
        }
      } catch (error) {
        console.error('Ban check failed:', error);
        // On network error, try a backup check
        try {
          const backupResponse = await fetch(`${API_URL}/api/health`);
          if (backupResponse.status === 403) {
            setIsBanned(true);
          }
        } catch (e) {
          // Ignore backup errors
        }
      } finally {
        setChecking(false);
      }
    };

    checkBan();
  }, []);

  return { isBanned, checking };
};

// Heartbeat hook - sends user activity to backend every 30 seconds
const useHeartbeat = () => {
  const { user, token } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        if (user && token) {
          // Logged-in user heartbeat
          await fetch(`${API_URL}/api/heartbeat`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ page: location.pathname })
          });
        } else {
          // Anonymous visitor heartbeat
          await fetch(`${API_URL}/api/heartbeat-anonymous`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ page: location.pathname })
          });
        }
      } catch (error) {
        // Silently fail - heartbeat is not critical
      }
    };

    // Send immediately
    sendHeartbeat();

    // Then every 30 seconds
    const interval = setInterval(sendHeartbeat, 30000);

    return () => clearInterval(interval);
  }, [user, token, location.pathname]);
};

// Page view tracker - custom internal analytics
const usePageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    let sid = sessionStorage.getItem('aqm_sid');
    if (!sid) {
      sid = (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
      sessionStorage.setItem('aqm_sid', sid);
    }
    fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: location.pathname, referrer: document.referrer || '', session_id: sid })
    }).catch(() => {});
  }, [location.pathname]);
};

// Component to wrap heartbeat hook
const HeartbeatTracker = () => {
  useHeartbeat();
  usePageTracker();
  return null;
};

// Wrapper component that checks IP ban
const AppWithBanCheck = ({ children }) => {
  const { isBanned, checking } = useIPBanCheck();

  if (checking) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isBanned) {
    return <BannedPage />;
  }

  return children;
};

// Quick category summary cards for homepage
const CategorySummary = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const categories = [
    {
      id: 'services',
      title: 'Services',
      description: 'Windows & BIOS',
      icon: Settings,
      path: '/services',
      color: 'from-cyan-600/20 to-cyan-900/10',
      borderColor: 'border-cyan-600/30 hover:border-cyan-500',
      iconBg: 'bg-cyan-600/20',
      items: ['Service Windows - 19€/23€', 'Pack Complet - 32€/40€']
    },
    {
      id: 'benchmark',
      title: 'Benchmark',
      description: 'Avant / Après optimisation',
      icon: TrendingUp,
      path: '/benchmark',
      color: 'from-orange-600/20 to-orange-900/10',
      borderColor: 'border-orange-600/30 hover:border-orange-500',
      iconBg: 'bg-orange-600/20',
      items: ['+45% FPS moyen', '-60% latence', 'Résultats garantis']
    },
    {
      id: 'reviews',
      title: 'Avis Clients',
      description: 'Ce que disent nos clients',
      icon: Star,
      path: '/feedbacks',
      color: 'from-yellow-600/20 to-yellow-900/10',
      borderColor: 'border-yellow-600/30 hover:border-yellow-500',
      iconBg: 'bg-yellow-600/20',
      items: ['+1700 clients', '4.9/5 satisfaction', '100% recommandé']
    }
  ];

  return (
    <section className="py-10 md:py-16 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <AnimateOnScroll animation="fade-down" className="text-center mb-8 md:mb-12">
          <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-3 md:mb-4">
            Nos Services
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 md:mb-3">
            Tout ce dont vous avez <span className="text-cyan-500">besoin</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto text-sm md:text-base px-4">
            Optimisations professionnelles pour maximiser vos performances gaming
          </p>
        </AnimateOnScroll>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {categories.map((cat, index) => {
            const Icon = cat.icon;
            return (
              <AnimateOnScroll key={cat.id} animation="fade-up" delay={index * 80}>
                <button
                  onClick={() => navigate(cat.path)}
                  className={`group relative bg-gradient-to-br ${cat.color} border ${cat.borderColor} rounded-xl p-3 md:p-5 text-left transition-all duration-500 md:hover:scale-105 w-full h-full overflow-hidden
                    hover:shadow-[0_0_30px_rgba(6,182,212,0.15)]`}
                >
                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg ${cat.iconBg} flex items-center justify-center mb-2 md:mb-4 transition-all duration-300 group-hover:scale-110`}>
                    <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>
                  
                  {/* Title & Description */}
                  <h3 className="text-white font-bold text-sm md:text-lg mb-0.5 md:mb-1 group-hover:text-cyan-50 transition-colors">{cat.title}</h3>
                  <p className="text-gray-400 text-[10px] md:text-sm mb-2 md:mb-4 line-clamp-2">{cat.description}</p>
                  
                  {/* Items - Hidden on mobile */}
                  <ul className="hidden md:block space-y-1.5 mb-4">
                    {cat.items.map((item, idx) => (
                      <li key={idx} className="text-gray-500 text-xs flex items-center gap-2 group-hover:text-gray-400 transition-colors">
                        <div className="w-1 h-1 bg-cyan-500 rounded-full group-hover:scale-125 transition-transform"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                  
                  {/* Arrow */}
                  <div className="flex items-center gap-1 text-cyan-400 text-[10px] md:text-sm font-medium group-hover:gap-2 transition-all">
                    Voir plus <ChevronRight className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Quick Stats Section
const QuickStats = () => {
  const stats = [
    { value: '+1700', label: 'PCs optimisés', icon: Star },
    { value: '+1700', label: 'Clients satisfaits', icon: CheckCircle },
    { value: '+45%', label: 'FPS en moyenne', icon: Zap },
    { value: '< 30min', label: 'Délai moyen', icon: Clock }
  ];

  return (
    <section className="py-8 md:py-12 px-4 md:px-8 bg-zinc-900/30 border-y border-cyan-900/20">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <AnimateOnScroll key={idx} animation="fade-up" delay={idx * 200}>
                <div className="text-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 rounded-full bg-cyan-600/20 border border-cyan-500/30 flex items-center justify-center hover-glow">
                    <Icon className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" />
                  </div>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-0.5 md:mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-[10px] md:text-sm">{stat.label}</p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};


// Affiliate Tracker Component
const AffiliateTracker = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check for ?aff= parameter (old format, for backward compatibility)
    const params = new URLSearchParams(window.location.search);
    const affCodeParam = params.get('aff');
    
    // Check for /code format (new clean format)
    const pathname = window.location.pathname;
    const pathCode = pathname.split('/').filter(p => p)[0]; // Get first segment after /
    
    // Determine which format is being used
    const affCode = affCodeParam || (pathCode && !['services', 'benchmark', 'fps-estimator', 'faq', 'feedbacks', 'parrainage', 'affilies', 'mes-commandes', 'admin', 'espace-client', 'aqmoptitool', 'mon-affiliation', 'recrutement', 'espace-helper', 'support', 'conditions', 'cgu', 'cgv', 'confidentialite', 'mentions-legales', 'success', 'ma-licence', 'licences', 'suggestions', 'preview'].includes(pathCode) ? pathCode : null);
    
    if (affCode) {
      // Store affiliate code in localStorage for 30 days
      localStorage.setItem('affiliate_code', affCode);
      localStorage.setItem('affiliate_timestamp', Date.now().toString());
      console.log(`✅ Affiliate tracked: ${affCode}`);
      
      // Track click in backend
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/track-click/${affCode}`, {
        method: 'POST'
      }).catch(err => console.error('Error tracking affiliate click:', err));
      
      // Redirect to homepage with clean URL
      if (pathname !== '/') {
        navigate('/', { replace: true });
      } else if (affCodeParam) {
        window.history.replaceState({}, document.title, '/');
      }
    }
  }, [navigate]);
  
  return null;
};



// Discord-banned redirect popup - Shows when OAuth callback redirected with ?error=discord_banned
const DiscordBannedPopup = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err === 'discord_banned') {
      setShow(true);
      params.delete('error');
      const newSearch = params.toString();
      window.history.replaceState({}, document.title, `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`);
    }
  }, []);

  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[9999]" data-testid="discord-banned-popup">
      <div className="bg-[#0f1018] border border-red-500/40 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/15 flex items-center justify-center">
          <Ban className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">Compte banni du Discord</h2>
        <p className="text-gray-300 mb-6 leading-relaxed">
          Ton compte Discord est banni du serveur AqmOpti. Tu ne peux pas te connecter au site.
        </p>
        <a
          href="https://discord.gg/aqmopti"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-lg transition-all"
        >
          Contacter le support
        </a>
      </div>
    </div>
  );
};

// Success Popup Component - Shows everywhere when coming back from Stripe
const SuccessPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // The /success page handles its own display (AqmOptiTool license vs Windows flow)
    if (window.location.pathname.startsWith('/success')) return;
    // Check if URL has session_id parameter (from Stripe redirect)
    const searchParams = new URLSearchParams(window.location.search);
    const sessionId = searchParams.get('session_id');
    
    // Check if we already showed the popup for this session
    const shownSessions = JSON.parse(localStorage.getItem('shown_success_sessions') || '[]');
    
    if (sessionId && !shownSessions.includes(sessionId)) {
      // Show popup
      setShowPopup(true);
      
      // Mark this session as shown
      shownSessions.push(sessionId);
      localStorage.setItem('shown_success_sessions', JSON.stringify(shownSessions.slice(-10))); // Keep last 10
      
      // Clean URL
      searchParams.delete('session_id');
      const newSearch = searchParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`;
      window.history.replaceState({}, document.title, newUrl);
      
      // Auto close after 15 seconds
      setTimeout(() => {
        setShowPopup(false);
      }, 15000);
    }
  }, []);

  const handleClose = () => {
    setShowPopup(false);
  };

  if (!showPopup) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[9999] pointer-events-none"
      style={{ animation: 'fadeIn 0.4s ease-out' }}
    >
      <div 
        className="bg-[#0f1018] border border-cyan-800/40 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden relative pointer-events-auto"
        style={{ animation: 'scaleIn 0.5s ease-out' }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-300 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Animated Checkmark */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle 
                  className="w-12 h-12 text-emerald-400 animate-check" 
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-white mb-4 flex items-center justify-center gap-3">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
            Commande Validée !
          </h2>

          {/* Message */}
          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            Suivez les tutoriels pour installer Windows et indiquez lorsque vous avez finis dans votre ticket Discord.
          </p>

          {/* Info Box */}
          <div className="bg-cyan-950/30 border border-cyan-700/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-cyan-300 font-medium">
              🎫 Un ticket Discord a été créé automatiquement !
            </p>
          </div>

          {/* Action Button */}
          <button
            onClick={() => {
              handleClose();
              navigate('/espace-client');
            }}
            className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
          >
            Accéder à l'Espace Client
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* Auto-close notice */}
          <p className="text-gray-500 text-xs mt-4">
            Fermeture automatique dans 15 secondes...
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          0% {
            transform: scale(0.95);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.05);
            opacity: 0.9;
          }
        }

        /* Smooth animation for checkmark */
        .animate-check {
          will-change: transform;
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Login Prompt Popup - Shows after 10 seconds if user is not logged in
// After 1 minute, shows mandatory popup that cannot be dismissed
const LoginPromptPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);
  const { user, loading } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const location = useLocation();

  // Pages publiques accessibles sans connexion
  const publicPages = ['/recrutement', '/services', '/benchmark', '/faq', '/feedbacks', '/fps-estimator', '/support', '/conditions', '/cgu', '/cgv', '/confidentialite', '/mentions-legales', '/suggestions'];
  const isPublicPage = publicPages.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    // Wait for auth to load
    if (loading) return;
    
    // Don't show if user is already logged in
    if (user) {
      // Clear the first visit time when logged in
      localStorage.removeItem('first_visit_time');
      return;
    }

    // Ne pas afficher le popup forcé sur les pages publiques
    if (isPublicPage) {
      setShowPopup(false);
      return;
    }

    // Get or set first visit time
    let firstVisitTime = localStorage.getItem('first_visit_time');
    if (!firstVisitTime) {
      firstVisitTime = Date.now().toString();
      localStorage.setItem('first_visit_time', firstVisitTime);
    }

    const timeSinceFirstVisit = Date.now() - parseInt(firstVisitTime);
    const mandatoryDelay = 15 * 1000;  // 15 seconds before mandatory popup
    const softDelay = 7500;            // 7.5 seconds for first soft popup

    // If more than mandatoryDelay has passed since first visit, show mandatory popup immediately
    if (timeSinceFirstVisit >= mandatoryDelay) {
      setIsMandatory(true);
      setShowPopup(true);
      return;
    }

    // Show first popup after 15 seconds
    const firstTimer = setTimeout(() => {
      if (!dismissed) {
        setShowPopup(true);
      }
    }, Math.max(softDelay - timeSinceFirstVisit, 0));

    // Show mandatory popup after remaining time to reach 30 seconds
    const remainingTime = Math.max(mandatoryDelay - timeSinceFirstVisit, 0);
    const mandatoryTimer = setTimeout(() => {
      if (!user) {
        setIsMandatory(true);
        setShowPopup(true);
      }
    }, remainingTime);

    return () => {
      clearTimeout(firstTimer);
      clearTimeout(mandatoryTimer);
    };
  }, [user, loading, dismissed]);

  const handleClose = () => {
    // Can only close if not mandatory
    if (!isMandatory) {
      setShowPopup(false);
      setDismissed(true);
    }
  };

  const handleLogin = () => {
    window.location.href = `${API_URL}/api/auth/discord`;
  };

  // Don't show popup if user is logged in
  if (user) return null;
  
  // Ne pas afficher sur les pages publiques
  if (isPublicPage) return null;
  
  if (!showPopup) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[9998] pointer-events-none"
      style={{ animation: 'fadeIn 0.4s ease-out' }}
    >
      <div 
        className="bg-[#0f1018] border border-indigo-500/40 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden relative pointer-events-auto"
        style={{ animation: 'scaleIn 0.5s ease-out' }}
      >
        {/* Close Button - Only show if not mandatory */}
        {!isMandatory && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-300 transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>
        )}

        {/* Content */}
        <div className="p-8 text-center">
          {/* Discord Icon */}
          <div className="mb-6 flex justify-center">
            <div className={`w-20 h-20 rounded-full ${isMandatory ? 'bg-red-500/20' : 'bg-indigo-500/20'} flex items-center justify-center`}>
              <svg className={`w-12 h-12 ${isMandatory ? 'text-red-400' : 'text-indigo-400'}`} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-3">
            {isMandatory ? 'Connexion Obligatoire' : 'Connecte-toi avec Discord !'}
          </h2>

          {/* Message */}
          <p className="text-gray-300 text-base leading-relaxed mb-6">
            {isMandatory 
              ? 'Pour continuer à naviguer sur le site, tu dois te connecter avec ton compte Discord.'
              : 'Pour profiter de toutes les fonctionnalités et suivre tes commandes, connecte-toi avec ton compte Discord.'
            }
          </p>

          {/* Benefits */}
          <div className={`${isMandatory ? 'bg-red-950/30 border-red-700/30' : 'bg-indigo-950/30 border-indigo-700/30'} border rounded-lg p-4 mb-6 text-left`}>
            <p className={`text-sm ${isMandatory ? 'text-red-300' : 'text-indigo-300'} font-medium mb-2`}>
              {isMandatory ? 'Pourquoi ?' : 'Avantages :'}
            </p>
            <ul className="text-sm text-gray-400 space-y-1">
              {isMandatory ? (
                <>
                  <li>• Sécurise ton expérience sur le site</li>
                  <li>• Accède à toutes les fonctionnalités</li>
                  <li>• Passe tes commandes facilement</li>
                </>
              ) : (
                <>
                  <li>• Obtiens ton code de parrainage unique</li>
                  <li>• Accède à tes statistiques de parrainage</li>
                </>
              )}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleLogin}
              className={`w-full ${isMandatory ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2`}
            >
              <LogIn className="w-5 h-5" />
              Se connecter avec Discord
            </button>
            
            {!isMandatory && (
              <button
                onClick={handleClose}
                className="w-full text-gray-400 hover:text-gray-300 text-sm transition-colors"
              >
                Plus tard
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          0% { transform: scale(0.95); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};


const Home = ({ activeSection, setActiveSection }) => {
  const { t } = useLanguage();
  
  return (
    <>
      {/* Main CTA - Présentation principale */}
      <MainCTA />
      
      {/* Hero Section - Les 3 étapes */}
      <HeroSection />
      
      {/* Quick Stats */}
      <QuickStats />
      
      {/* Category Summary */}
      <CategorySummary />
      
      {/* Advantages */}
      <AdvantagesSection />

      {/* Compatible Games */}
      <CompatibleGamesSection />

      {/* Benchmark preview teaser (tweakslab-style) */}
      <BenchmarkPreview />
      
      {/* Final CTA */}
      <FinalCTA />
    </>
  );
};

function App() {
  const [activeSection, setActiveSection] = useState('/');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <AppWithBanCheck>
      <AuthProvider>
        <LanguageProvider>
          <SiteStatsProvider>
          <div className="App">
            <BrowserRouter>
            <MaintenanceGate>

            {/* Affiliate Tracking */}
            <AffiliateTracker />

            {/* Success Popup - Shows after Stripe payment */}
            <SuccessPopup />

            {/* Discord-banned popup — shown when OAuth callback returned ?error=discord_banned */}
            <DiscordBannedPopup />

            {/* Global modal triggered by 403 not_in_discord / discord_banned from any checkout/ticket flow */}
            <DiscordRequiredModal />

            {/* Login Prompt - Shows after 10s if not logged in */}
            <LoginPromptPopup />

            {/* Animated Background */}
            <AnimatedBackground />
            
            {/* Particles Background - Subtle floating particles */}
            <ParticlesBackground />
            
            {/* Heartbeat tracker for active users */}
            <HeartbeatTracker />
            
            {/* Flash Sale Banner */}
            <FlashSaleBanner />

            {/* Sticky bottom CTA — homepage only */}
            <StickyCTABanner />
            
            <div className="min-h-screen bg-[#0a0b14] relative">
              {/* Sidebar */}
              <Sidebar 
                activeSection={activeSection} 
                setActiveSection={setActiveSection}
                mobileMenuOpen={mobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
              />
              
              {/* Main Content */}
              <div id="main-wrapper" className="main-content-wrapper min-h-screen w-full lg:ml-52">
                {/* Header */}
                <Header onMenuClick={() => setMobileMenuOpen(true)} />
                
                {/* Content */}
                <main className="pt-14 lg:pt-16 min-h-screen w-full">
                  <LiveFlashBar />
                  <Routes>
                    <Route path="/" element={<Home activeSection={activeSection} setActiveSection={setActiveSection} />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/benchmark" element={<BenchmarkPage />} />
                    <Route path="/fps-estimator" element={<FPSEstimatorPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/parrainage" element={<ReferralPage />} />
                    <Route path="/invite/:code" element={<InviteRedirect />} />
                    <Route path="/mes-commandes" element={<MesCommandesPage />} />
                    <Route path="/success" element={<SuccessPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/feedbacks" element={<FeedbacksPage />} />
                    <Route path="/espace-client" element={<EspaceClientPage />} />
                    <Route path="/ma-licence" element={<MaLicencePage />} />
                    <Route path="/licences" element={<LicencesPage />} />
                    <Route path="/aqmoptitool" element={<AqmOptiToolPage />} />
                    <Route path="/mon-affiliation" element={<MyAffiliatePage />} />
                    <Route path="/recrutement" element={<RecruitmentPage />} />
                    <Route path="/suggestions" element={<SuggestionsPage />} />
                    <Route path="/espace-helper" element={<HelperSpacePage />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/conditions" element={<TermsPage />} />
                    <Route path="/cgu" element={<TermsPage />} />
                    <Route path="/cgv" element={<SalesTermsPage />} />
                    <Route path="/confidentialite" element={<PrivacyPage />} />
                    <Route path="/mentions-legales" element={<LegalNoticePage />} />
                    <Route path="/:affiliateCode" element={<Home activeSection={activeSection} setActiveSection={setActiveSection} />} />
                  </Routes>
                </main>
                
                {/* Footer */}
                <footer className="bg-[#060610] border-t border-cyan-900/30 mt-8" data-testid="site-footer">
                  {/* Bottom row */}
                  <div className="py-6 px-4">
                    <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                      <div className="flex items-center gap-3">
                        <img
                          src="/aqmopti-logo.png"
                          alt="AqmOpti"
                          className="h-9 w-9 object-contain rounded-lg"
                          style={{ mixBlendMode: 'lighten' }}
                        />
                        <span className="text-gray-500 text-xs md:text-sm">© 2026 AqmOpti. Tous droits réservés.</span>
                      </div>
                      <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs md:text-sm">
                        <Link to="/cgu" className="text-gray-400 hover:text-cyan-400 transition-colors">
                          CGU
                        </Link>
                        <Link to="/cgv" className="text-gray-400 hover:text-cyan-400 transition-colors">
                          CGV
                        </Link>
                        <Link to="/confidentialite" className="text-gray-400 hover:text-cyan-400 transition-colors">
                          Confidentialité
                        </Link>
                        <Link to="/mentions-legales" className="text-gray-400 hover:text-cyan-400 transition-colors">
                          Mentions légales
                        </Link>
                      </div>
                    </div>
                  </div>
                </footer>
              </div>
            </div>
            </MaintenanceGate>
          </BrowserRouter>
          </div>
          </SiteStatsProvider>
        </LanguageProvider>
      </AuthProvider>
    </AppWithBanCheck>
  );
}

export default App;